<template>
  <div id="image-sidenav">
    <input ref="imageRef" type="file" accept="image/*" @change="handleFile" hidden />

    <Button
      icon="fas fa-image"
      :label="$t('change-image')"
      class="image-sidenav__button"
      @click="$refs.imageRef.click()"
    />

    <Button
      v-if="options.url && options.url !== 'https://via.placeholder.com/100?text=NO+IMAGE'"
      icon="fas fa-crop-alt"
      :label="$t('crop')"
      class="image-sidenav__button"
      @click="setOverlay({ component: 'ImageCrop', data: { image: croppieImage, widget } })"
    />

    <strong>{{ $t('size') }}</strong>
    <Toggle
      :label="$t('fit-to-panel')"
      :value="!options.staticImg"
      class="mt-2"
      @toggle="propChanged('staticImg', !$event)"
    />

    <div class="d-flex tlw-flex-row justify-content-between mb-3 mt-3">
      <div class="placement">
        <span class="text-capitalize">{{ $t('width') }} (px)</span>
        <PlacementNumber
          :initial="imgWidth"
          @change="changeValues('width', $event)"
          :negative="false"
          :increment="25"
          :disabled="!options.staticImg"
        />
      </div>
      <div class="placement">
        <span class="text-capitalize">{{ $t('height') }} (px)</span>
        <PlacementNumber
          :initial="imgHeight"
          @change="changeValues('height', $event)"
          :negative="false"
          :disabled="!options.staticImg"
        />
      </div>
    </div>
    <div class="d-flex tlw-flex-row justify-content-between mb-3 mt-3" v-if="isAdmin">
      <Collapsible class="advanced-collapsible" :label="$t('advanced-config')">
        <Style :id="widget.id" :code="options.style.scss" @change="styleChanged(`.image_${widget.id}`, $event)" />
      </Collapsible>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import WidgetSideNavMixin from '@/components/sidenav/landing/panel/widget/Mixin.js'
import { infoAlert, successAlert, errorAlert } from '@/helpers/alertHelper.js'
import UploadFileService from '@/services/UploadFile.js'
import jwt from '@/helpers/jwtHelper'

export default {
  name: 'ImageSideNav',
  i18nOptions: { keyPrefix: 'components.sidenav.landing.panel.widget.image-side-nav' },
  mixins: [WidgetSideNavMixin],
  data() {
    return {
      imgWidth: 0,
      imgHeight: 0,
      aspectRatio: 0,
    }
  },
  computed: {
    croppieImage() {
      return {
        url: this.widget.options.originalUrl,
        points: this.widget.options.points,
        orientation: this.widget.options.orientation,
        zoom: this.widget.options.zoom,
      }
    },
    isAdmin() {
      return jwt.isAdmin()
    },
  },
  updated() {
    this.widget.onImgLoaded = () => {
      this.imgWidth = this.widget.options.width
      this.imgHeight = this.widget.options.height
      this.aspectRatio = this.imgWidth / this.imgHeight
    }
  },
  created() {
    this.initValues()
    this.widget.onImgCropped = () => {
      if (this.widget.options.staticImg) this.propChanged('staticImg', false)
    }
  },
  methods: {
    ...mapActions('ui', ['setOverlay']),
    handleFile(event) {
      infoAlert(this.$i18n.t('upload-file.upload-init'))
      this.options.loading = true
      UploadFileService.uploadCore(event.target.files[0])
        .then(response => {
          const url = response.data.data.uploadAsset.url
          this.updateUrl(url)
          this.options.loading = false
          successAlert(this.$i18n.t('upload-file.success'))
        })
        .catch(() => {
          this.options.loading = false
          errorAlert(this.$i18n.t('upload-file.error'))
        })
    },
    updateUrl(url) {
      this.propChanged('', {
        ...this.widget.options,
        url,
        originalUrl: url,
        staticImg: false,
      })
    },
    changeValues(type, value) {
      if (type === 'width') {
        this.imgWidth = value
        this.imgHeight = Math.ceil(value / this.aspectRatio)
      } else {
        this.imgWidth = Math.ceil(value * this.aspectRatio)
        this.imgHeight = value
      }
      this.propChanged('', {
        ...this.widget.options,
        width: this.imgWidth,
        height: this.imgHeight,
      })
    },
    initValues() {
      this.imgWidth =
        this.widget.options.staticImg && this.widget.options.width
          ? this.widget.options.width
          : document.getElementById(this.widget.id).width
      this.imgHeight =
        this.widget.options.staticImg && this.widget.options.height
          ? this.widget.options.height
          : document.getElementById(this.widget.id).height

      this.aspectRatio = this.imgWidth / this.imgHeight
    },
  },
}
</script>

<style lang="scss" scoped>
.placement {
  width: 135px;
}

.image-sidenav__button {
  margin-bottom: 16px;
}
</style>
